import type { HistoryTone } from '@/Ship/Components/History'

/**
 * Вид події журналу вузла диска — за ним стрічка вирішує, що показати в
 * подробицях (`DISK-001` §4.14, `payload` — опис enum беку).
 */
export type DiskNodeHistoryKind =
    | 'created'
    | 'renamed'
    | 'description'
    | 'moved'
    | 'copied'
    | 'uploaded'
    | 'version'
    | 'trashed'
    | 'restored'
    | 'purged'
    | 'shared'
    | 'shareChanged'
    | 'unshared'
    | 'transferred'

export interface DiskNodeHistoryEventInfo {
    label: string
    tone: HistoryTone
    kind: DiskNodeHistoryKind
}

/**
 * Коди подій журналу вузла диска — рівно enum беку
 * `backend/web/src/Containers/DiskContainer/Database/Enum/DiskNodeHistoryEvent.php`.
 *
 * `disk_node_purged` лежить у журналі **вихідної папки** (або кореня
 * простору): самого вузла після остаточного видалення вже немає.
 *
 * Ключі — **буквальні** коди: мапу звіряє з беком вартовий «кожен код події
 * історії з бека має підпис на фронті» (`Ship/Helpers/architectureGuard.test.ts`).
 */
export const DISK_NODE_HISTORY_EVENT_MAP: Record<string, DiskNodeHistoryEventInfo> = {
    disk_node_created: { label: 'Папку створено', tone: 'blue', kind: 'created' },
    disk_node_renamed: { label: 'Перейменовано', tone: 'neutral', kind: 'renamed' },
    disk_node_description_changed: { label: 'Змінено опис', tone: 'neutral', kind: 'description' },
    disk_node_moved: { label: 'Переміщено', tone: 'purple', kind: 'moved' },
    disk_node_copied: { label: 'Скопійовано', tone: 'cyan', kind: 'copied' },
    disk_node_uploaded: { label: 'Файл завантажено', tone: 'blue', kind: 'uploaded' },
    disk_node_version_added: { label: 'Нова версія', tone: 'geekblue', kind: 'version' },
    disk_node_trashed: { label: 'Переміщено в кошик', tone: 'orange', kind: 'trashed' },
    disk_node_restored: { label: 'Відновлено з кошика', tone: 'green', kind: 'restored' },
    disk_node_purged: { label: 'Видалено назавжди', tone: 'red', kind: 'purged' },
    // Доступи (фаза 2): адресат, роль і строк — у `payload`
    disk_node_shared: { label: 'Надано доступ', tone: 'green', kind: 'shared' },
    disk_node_share_changed: { label: 'Змінено доступ', tone: 'gold', kind: 'shareChanged' },
    disk_node_unshared: { label: 'Доступ прибрано', tone: 'orange', kind: 'unshared' },
    // Справа звільнення (`DISK-006` §4.4, §6.3): передано наступнику чи у відділ, або повернуто
    disk_node_transferred: { label: 'Передано новому власнику', tone: 'purple', kind: 'transferred' },
}
